import type { QueryClient } from '@tanstack/react-query';

/**
 * Cross-feature React Query invalidation for the Configuración ABMs.
 *
 * Each ABM CRUD already invalidates its OWN listing query on save —
 * that re-renders the table the user is looking at. But several
 * features cache derived data with their own staleTime windows
 * (Compras article picker, Cotización picker, ArticuloDialog cuenta
 * contable selector, Pagar Factura medios catalog, …). Without
 * explicit invalidation those views stay stale until their staleTime
 * expires (5 minutes for the heaviest ones), which surfaces as "I
 * just added X but it doesn't show in Compras / Cotización" bug
 * reports.
 *
 * Call `invalidateAbmRelatedQueries(qc, 'articulos')` (or the relevant
 * abm tag) from the `onSuccess` of every create / update / delete
 * mutation. The helper fires `qc.invalidateQueries` for every cache
 * key whose contents depend on that ABM. Keys are matched as
 * prefixes by React Query, so `['compras', 'articulos-comprables']`
 * also catches sub-keys like `['compras', 'articulos-comprables', id]`.
 *
 * ## Maintenance
 * When you add a new feature query that reads from one of these ABMs,
 * add its queryKey prefix to the relevant list below. When you add a
 * new ABM, add a new entry here AND wire the relevant CRUDs to call
 * this helper.
 */
export type AbmKind =
  | 'articulos'
  | 'categorias'
  | 'cuentas-contables'
  | 'proveedores'
  | 'medios-de-pago'
  | 'medios-contacto'
  | 'percepciones'
  | 'subcategorias'
  | 'parametros-sistema'
  | 'ef-items'
  | 'ef-subitems';

const DEPENDENCY_MAP: Record<AbmKind, ReadonlyArray<readonly unknown[]>> = {
  // Articles drive the Compras 3-level cascade (Categoría → Cuenta
  // Contable → Descripción) and the Ventas cotización picker. Both
  // cache aggressively — staleTime 5 min on the Compras side.
  articulos: [
    ['configuracion', 'articulos'], // own SI/NO listings
    ['compras', 'articulos-comprables'],
    ['nueva-cotizacion', 'articulos'],
    ['editar-cotizacion', 'articulos'],
  ],
  // Categories feed the picker on every articulos dialog (Productos,
  // Servicios) and a categoria/subcategoria explorer on Tesorería.
  categorias: [
    ['configuracion', 'categorias'], // own listing + 'active' subkey
    ['tesoreria', 'categorias-subcategorias'],
  ],
  // Cuentas contables show up in the Articulos dialog dropdowns and
  // also indirectly in the Compras cascade via articles' join data.
  'cuentas-contables': [
    ['configuracion', 'cuentas-contables'],
  ],
  proveedores: [
    ['configuracion', 'proveedores'],
    ['compras', 'proveedores-activos'],
  ],
  'medios-de-pago': [
    ['configuracion', 'medios-de-pago'],
    ['medios-de-pago', 'activos'],
    ['pagos', 'medios-de-pago'],
    // La moneda de cada caja (toggle "Caja en dólares") se ve en Tesorería.
    ['tesoreria'],
  ],
  // Medios de Contacto only feeds the Contactos detail page; no
  // cross-feature cache. Listed for symmetry.
  'medios-contacto': [
    ['configuracion', 'medios-contacto'],
  ],
  percepciones: [
    ['configuracion', 'percepciones-impuesto'],
    ['compras', 'percepciones-catalogo'],
  ],
  subcategorias: [
    ['tesoreria', 'categorias-subcategorias'],
  ],
  // Develop-only ABM. Parámetros del Sistema rows back the country
  // pack's runtime constants (Limite Nova, Comision Nova, etc.). The
  // server reads them on each request via getNumericParam, but the FE
  // also caches the listing for the CRUD itself — invalidate so the
  // list reflects the just-saved value.
  'parametros-sistema': [
    ['configuracion', 'parametros-sistema'],
  ],
  // EF Items feed the EF SubItem parent dropdown; refresh both listings.
  // Cuentas Contables show the derived EF Item/SubItem names, so refresh
  // those too when the EF taxonomy changes.
  'ef-items': [
    ['configuracion', 'ef-items'],
    ['configuracion', 'cuentas-contables'],
  ],
  'ef-subitems': [
    ['configuracion', 'ef-subitems'],
    ['configuracion', 'cuentas-contables'],
  ],
};

export function invalidateAbmRelatedQueries(
  qc: QueryClient,
  abm: AbmKind,
): void {
  for (const key of DEPENDENCY_MAP[abm]) {
    qc.invalidateQueries({ queryKey: key as readonly unknown[] });
  }
}
